import {Trans} from '@lingui/macro';
import React, {memo, ReactNode, useRef} from 'react';
import styled, {css} from 'styled-components';

import {buildHoverParentClassName} from '../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {buildInteractionIdProps} from '../../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownItemProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {InteractiveListItem} from '../../../../../libs/shared/ui/src/components/interactiveList/interactiveListItem';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {isTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {AnswerModel} from '../../../../models/api/answerModel';
import {VariableInterpolator} from '../../../../services/variableInterpolation/variableInterpolator';
import {AnswersDropdownRowContent} from './answersDropdownRowContent';
import {AnswerPreview} from './preview/answerPreview';

/*
 * Props.
 */

export interface AnswersDropdownRowProps extends DropdownItemProps {
  answer: AnswerModel;
  folderBreadcrumbs: ReadonlyArray<ReactNode>;
  interpolator: VariableInterpolator;
  onClick: Handler;
  onDropdownClose?: Handler;
  isSearchResult: boolean;
  replacePilledVariablesInMessageTemplates: boolean;
}

/*
 * Styles.
 */

interface AnswersDropdownItemStyleProps {
  $isHighlighted: boolean;
}

export const StyledAnswersDropdownItemDiv = styled.div<AnswersDropdownItemStyleProps>`
  justify-items: start;
  padding: 5px 12px;

  ${(p) => maybeHighlightedStyle(p.$isHighlighted)}
`;

function maybeHighlightedStyle(isHighlighted: boolean) {
  if (!isHighlighted) {
    return '';
  }

  return css`
    background-color: ${(p) => p.theme.alphas.gray10};
  `;
}

export const StyledCircleSeperatorSpan = styled.span`
  display: inline-block;
  vertical-align: middle;
  border-radius: 100px;
  background-color: ${(p) => p.theme.greys.shade70};
  margin: 1px 1px 1px 1px;
  width: 10px;
  min-width: 10px;
  height: 10px;
  transform: scale(0.35); // Without this, the "circle" becomes more of a rounded rectangle.
`;

const StyledUnfiledPillSpan = styled.span`
  display: inline-block;
  color: ${(p) => p.theme.greys.shade70};
  background-color: ${(p) => p.theme.greys.shade30};
  font-weight: ${fontWeights.bold};
  font-size: 8px;
  line-height: 12px;
  border-radius: 8px;
  padding: 1px 4px;
  margin-right: 4px;
  text-transform: uppercase;
`;

/*
 * Component.
 */

export const AnswersDropdownRow = memo<AnswersDropdownRowProps>((props) => {
  const {
    answer,
    isSearchResult,
    folderBreadcrumbs,
    className,
    index,
    onClick,
    interpolator,
    onDropdownClose,
    replacePilledVariablesInMessageTemplates,
  } = props;
  const rowRef = useRef<HTMLDivElement>(null);

  const isPrivateAnswer = isTeammateNamespace(answer.namespace);

  return (
    <InteractiveListItem onSelect={onClick} index={index}>
      {({itemProps, isHighlighted}) => (
        <>
          <div ref={rowRef}>
            <StyledAnswersDropdownItemDiv
              {...itemProps} /* eslint-disable-line react/jsx-props-no-spreading */
              className={buildHoverParentClassName(isHighlighted, className)}
              $isHighlighted={isHighlighted}
              {...buildInteractionIdProps(
                props.interactionId,
              )} /* eslint-disable-line react/jsx-props-no-spreading */
            >
              <AnswersDropdownRowContent
                title={answer.name}
                description={
                  isSearchResult
                    ? renderDescriptionWithBreadcrumbs(answer.blurb, folderBreadcrumbs)
                    : answer.blurb
                }
                iconName="messageTemplateSmall"
                isPrivate={isPrivateAnswer}
              />
            </StyledAnswersDropdownItemDiv>
          </div>
          {isHighlighted && rowRef.current && (
            <AnswerPreview
              anchor={rowRef}
              answer={answer}
              interpolator={interpolator}
              onSelect={onClick}
              onDropdownClose={onDropdownClose}
              replacePilledVariablesInMessageTemplates={replacePilledVariablesInMessageTemplates}
            />
          )}
        </>
      )}
    </InteractiveListItem>
  );
});

const unfiledCopy = <Trans id="answer-search-result-unfiled">UNFILED</Trans>;

export function renderDescriptionWithBreadcrumbs(
  description: ReactNode,
  folderBreadcrumbs: ReadonlyArray<ReactNode>,
) {
  const isUnfiled = folderBreadcrumbs.length === 0;

  const renderUnfiledPill = () => <StyledUnfiledPillSpan>{unfiledCopy}</StyledUnfiledPillSpan>;

  const renderBreadcrumbs = () => (
    <>
      <Trans id="folder-breadcrumb-label-in">in</Trans> {folderBreadcrumbs}
    </>
  );

  const renderDescriptionTooltip = () => (
    <>
      {isUnfiled ? unfiledCopy : renderBreadcrumbs()}
      <StyledCircleSeperatorSpan />
      {description}
    </>
  );

  const renderDescriptionContent = () => (
    <>
      {isUnfiled ? (
        renderUnfiledPill()
      ) : (
        <>
          {renderBreadcrumbs()}
          <StyledCircleSeperatorSpan />
        </>
      )}
      {description}
    </>
  );

  return (
    <TooltipCoordinator render={renderDescriptionTooltip}>
      <TooltipOverflowCondition>{renderDescriptionContent()}</TooltipOverflowCondition>
    </TooltipCoordinator>
  );
}
